import React, { useState } from 'react';
import { useAppStore } from '@/store';
import { useToastContext } from '@/contexts/ToastContext';
import { adminDashboardService } from '@/api/adminServices';
import { Transaction } from '@/types';
import {
  CheckCircle,
  Clock,
  RefreshCw,
  Search,
  Eye,
  Phone,
  User,
  Receipt,
  X,
  Shield,
  Coins,
} from 'lucide-react';

interface TransactionFilters {
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | '';
  payment_method: 'DIRECT' | 'WAKALA' | '';
}

const cardClass = 'rounded-2xl border border-gray-200 bg-white p-4 shadow-sm';

const TransactionsPage: React.FC = () => {
  const { user } = useAppStore();
  const { showError, showSuccess } = useToastContext();

  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [filters, setFilters] = useState<TransactionFilters>({ status: '', payment_method: '' });
  const [search, setSearch] = useState('');

  const [detailTx, setDetailTx] = useState<Transaction | null>(null);
  const [approveTx, setApproveTx] = useState<Transaction | null>(null);
  const [rejectTx, setRejectTx] = useState<Transaction | null>(null);
  const [note, setNote] = useState('');
  const [rejectNote, setRejectNote] = useState('');

  const loadTransactions = async (showSpinner = false) => {
    try {
      if (showSpinner) setRefreshing(true);
      const response = await adminDashboardService.billing.getTransactions({
        status: filters.status || undefined,
        payment_method: filters.payment_method || undefined,
      });
      const data = Array.isArray(response.data) ? response.data : [];
      setTransactions(data);
    } catch (error) {
      console.error('Failed to load transactions:', error);
      showError('Failed to load transactions');
      setTransactions([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  React.useEffect(() => {
    if (!user) return;
    if (!user.is_staff) {
      showError('Access denied. Staff privileges required.');
      return;
    }
    loadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  React.useEffect(() => {
    if (user?.is_staff) {
      setLoading(true);
      loadTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.payment_method]);

  const filteredTransactions = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return transactions;
    return transactions.filter((t) => {
      const haystack = [
        t.id,
        t.user_full_name,
        t.user,
        t.user_phone_number,
        t.sender_name,
        t.wakala_name,
        t.transaction_status,
        t.payment_method,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [transactions, search]);

  const stats = React.useMemo(() => {
    return {
      total: filteredTransactions.length,
      pending: filteredTransactions.filter((t) => t.transaction_status === 'PENDING').length,
      approved: filteredTransactions.filter((t) => t.transaction_status === 'APPROVED').length,
      rejected: filteredTransactions.filter((t) => t.transaction_status === 'REJECTED').length,
    };
  }, [filteredTransactions]);

  const handleApprove = async () => {
    if (!approveTx) return;
    try {
      setActionLoading(approveTx.id);
      const response = await adminDashboardService.billing.approveTransaction(approveTx.id, note);
      if (response.success) {
        showSuccess(response.message || 'Transaction approved successfully');
        setApproveTx(null);
        setNote('');
        await loadTransactions(true);
      } else {
        showError(response.message || 'Failed to approve transaction');
      }
    } catch (error: any) {
      showError(error?.message || 'Failed to approve transaction');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectTx) return;
    if (!rejectNote.trim()) {
      showError('Please provide a reason for rejection');
      return;
    }
    try {
      setActionLoading(rejectTx.id);
      const response = await adminDashboardService.billing.rejectTransaction(rejectTx.id, rejectNote.trim());
      if (response.success) {
        showSuccess(response.message || 'Transaction rejected successfully');
        setRejectTx(null);
        setRejectNote('');
        await loadTransactions(true);
      } else {
        showError(response.message || 'Failed to reject transaction');
      }
    } catch (error: any) {
      showError(error?.message || 'Failed to reject transaction');
    } finally {
      setActionLoading(null);
    }
  };

  const statusBadge = (status: string) => {
    const config: Record<string, { className: string; Icon: typeof Clock }> = {
      PENDING: { className: 'bg-yellow-100 text-yellow-800', Icon: Clock },
      APPROVED: { className: 'bg-green-100 text-green-800', Icon: CheckCircle },
      REJECTED: { className: 'bg-red-100 text-red-800', Icon: X },
    };
    const { className, Icon } = config[status] || config.PENDING;
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${className}`}>
        <Icon className="h-3 w-3" />
        {status}
      </span>
    );
  };

  const formatCurrency = (amount: string | number) => `${parseFloat(String(amount)).toLocaleString()} TZS`;

  if (loading) {
    return <div className="p-6"><div className="flex justify-center py-16"><RefreshCw className="h-6 w-6 animate-spin text-orange-600" /></div></div>;
  }

  return (
    <div className="mx-auto max-w-7xl p-4 sm:p-6">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-100 text-orange-600">
            <Receipt className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Transactions</h1>
            <p className="text-sm text-gray-500">Review, approve and reject payment transactions</p>
          </div>
        </div>
        <button
          onClick={() => loadTransactions(true)}
          disabled={refreshing}
          className="flex items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className={cardClass}>
          <p className="text-xs font-medium text-gray-500">Total</p>
          <p className="mt-1 text-xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className={cardClass}>
          <p className="text-xs font-medium text-gray-500">Pending</p>
          <p className="mt-1 text-xl font-bold text-yellow-600">{stats.pending}</p>
        </div>
        <div className={cardClass}>
          <p className="text-xs font-medium text-gray-500">Approved</p>
          <p className="mt-1 text-xl font-bold text-green-600">{stats.approved}</p>
        </div>
        <div className={cardClass}>
          <p className="text-xs font-medium text-gray-500">Rejected</p>
          <p className="mt-1 text-xl font-bold text-red-600">{stats.rejected}</p>
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <select
          value={filters.status}
          onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value as any }))}
          className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
        >
          <option value="">All Status</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>
        <select
          value={filters.payment_method}
          onChange={(e) => setFilters((prev) => ({ ...prev, payment_method: e.target.value as any }))}
          className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
        >
          <option value="">All Methods</option>
          <option value="DIRECT">Direct</option>
          <option value="WAKALA">Wakala</option>
        </select>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, phone, sender or ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-xl border border-gray-300 bg-white py-2 pl-9 pr-10 text-sm text-gray-900"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-gray-400 hover:bg-gray-100"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Amount & Tokens</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Method</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Date</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredTransactions.map((t) => (
                <tr
                  key={t.id}
                  onClick={() => setDetailTx(t)}
                  className="cursor-pointer hover:bg-gray-50"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-orange-600">
                        <User className="h-4 w-4 text-white" />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900">{t.user_full_name}</p>
                        <p className="text-xs text-gray-500">@{t.user}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-900">{formatCurrency(t.amount)}</p>
                    <p className="text-xs text-gray-500">{t.tokens_generated} tokens</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                      {t.payment_method}
                    </span>
                    {t.wakala_name && <p className="mt-1 text-xs text-gray-500">via {t.wakala_name}</p>}
                  </td>
                  <td className="px-4 py-3">{statusBadge(t.transaction_status)}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{new Date(t.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); setDetailTx(t); }}
                        className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-50"
                        title="View details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      {t.transaction_status === 'PENDING' && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); setApproveTx(t); setNote(''); }}
                            disabled={actionLoading === t.id}
                            className="rounded-lg p-2 text-green-600 transition-colors hover:bg-green-50 disabled:opacity-50"
                            title="Approve"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setRejectTx(t); setRejectNote(''); }}
                            disabled={actionLoading === t.id}
                            className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
                            title="Reject"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredTransactions.length === 0 && (
          <div className="py-12 text-center">
            <Receipt className="mx-auto mb-3 h-10 w-10 text-gray-300" />
            <p className="text-sm text-gray-500">No transactions found</p>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {detailTx && (
        <Modal title="Transaction details" onClose={() => setDetailTx(null)}>
          <div className="space-y-3 text-sm">
            <DetailRow icon={<User className="h-4 w-4" />} label="User" value={`${detailTx.user_full_name} (${detailTx.user})`} />
            <DetailRow icon={<Phone className="h-4 w-4" />} label="Phone" value={detailTx.user_phone_number || '-'} />
            <DetailRow label="Sender name" value={detailTx.sender_name || '-'} />
            <DetailRow icon={<Coins className="h-4 w-4" />} label="Amount" value={formatCurrency(detailTx.amount)} />
            <DetailRow label="Tokens" value={`${detailTx.tokens_generated}`} />
            <DetailRow label="Method" value={`${detailTx.payment_method}${detailTx.wakala_name ? ` (${detailTx.wakala_name})` : ''}`} />
            <DetailRow icon={<Shield className="h-4 w-4" />} label="Status" value={detailTx.transaction_status} />
            <DetailRow label="Confirmed by" value={detailTx.confirmed_by_name || '-'} />
            <DetailRow label="Date" value={new Date(detailTx.created_at).toLocaleString()} />
            {detailTx.note && (
              <div className="rounded-xl bg-gray-50 p-3">
                <p className="text-xs font-semibold uppercase text-gray-500">Note</p>
                <p className="mt-1 text-gray-800">{detailTx.note}</p>
              </div>
            )}
          </div>

          {detailTx.transaction_status === 'PENDING' && (
            <div className="mt-5 flex justify-end gap-2 border-t border-gray-100 pt-4">
              <button
                onClick={() => {
                  const tx = detailTx;
                  setDetailTx(null);
                  setRejectNote('');
                  setRejectTx(tx);
                }}
                className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-100"
              >
                <X className="h-4 w-4" />
                Reject
              </button>
              <button
                onClick={() => {
                  const tx = detailTx;
                  setDetailTx(null);
                  setNote('');
                  setApproveTx(tx);
                }}
                className="flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700"
              >
                <CheckCircle className="h-4 w-4" />
                Approve
              </button>
            </div>
          )}
        </Modal>
      )}

      {/* Approve modal */}
      {approveTx && (
        <Modal title="Approve transaction" onClose={() => setApproveTx(null)}>
          <p className="text-sm text-gray-600">
            Approve payment of <span className="font-semibold">{formatCurrency(approveTx.amount)}</span> from{' '}
            <span className="font-semibold">{approveTx.user_full_name}</span>?
          </p>
          <label className="mt-4 block text-sm font-medium text-gray-700">Note (optional)</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Optional message to the user"
            className="mt-1 w-full rounded-xl border border-gray-300 p-2 text-sm focus:border-orange-500 focus:outline-none"
          />
          <ModalActions
            confirmLabel="Approve"
            confirmClass="bg-green-600 hover:bg-green-700"
            loading={actionLoading === approveTx.id}
            onCancel={() => setApproveTx(null)}
            onConfirm={handleApprove}
          />
        </Modal>
      )}

      {/* Reject modal */}
      {rejectTx && (
        <Modal title="Reject transaction" onClose={() => setRejectTx(null)}>
          <p className="text-sm text-gray-600">
            Reject payment of <span className="font-semibold">{formatCurrency(rejectTx.amount)}</span> from{' '}
            <span className="font-semibold">{rejectTx.user_full_name}</span>?
          </p>
          <label className="mt-4 block text-sm font-medium text-gray-700">Reason (required)</label>
          <textarea
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            rows={3}
            placeholder="Explain why this payment is being rejected"
            className="mt-1 w-full rounded-xl border border-gray-300 p-2 text-sm focus:border-orange-500 focus:outline-none"
          />
          <ModalActions
            confirmLabel="Reject"
            confirmClass="bg-red-600 hover:bg-red-700"
            loading={actionLoading === rejectTx.id}
            onCancel={() => setRejectTx(null)}
            onConfirm={handleReject}
          />
        </Modal>
      )}
    </div>
  );
};

const DetailRow: React.FC<{ icon?: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <div className="flex items-start justify-between gap-4 border-b border-gray-100 pb-2">
    <span className="flex items-center gap-2 text-gray-500">{icon}{label}</span>
    <span className="text-right font-medium text-gray-900">{value}</span>
  </div>
);

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
    <button className="absolute inset-0 bg-black/40" onClick={onClose} aria-label="Close" />
    <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100">
          <X className="h-5 w-5" />
        </button>
      </div>
      {children}
    </div>
  </div>
);

const ModalActions: React.FC<{
  confirmLabel: string;
  confirmClass: string;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ confirmLabel, confirmClass, loading, onCancel, onConfirm }) => (
  <div className="mt-5 flex justify-end gap-2">
    <button onClick={onCancel} className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
      Cancel
    </button>
    <button
      onClick={onConfirm}
      disabled={loading}
      className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${confirmClass}`}
    >
      {loading && <RefreshCw className="h-4 w-4 animate-spin" />}
      {confirmLabel}
    </button>
  </div>
);

export default TransactionsPage;
